// src/components/OrdersList.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Paper,
  Typography,
  Pagination,
  Box,
  TextField,
  Chip,
  TableSortLabel,
  TableContainer,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Grid,
  InputAdornment,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { getOrders, getOrderSmsStatusesBatch } from '../services/api';
import OrderDetailsModal from './modals/OrderDetailsModal';
import EditOrderModal from './modals/EditOrderModal';
import ResendOtpModal from './modals/ResendOtpModal';
import OrderTimelineModal from './modals/OrderTimelineModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { maskPhoneNumber } from '../utils/phoneUtils';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { neonBlue, shakespeare, kepple, california, stormGrey, redOrange } from '../theme/colors';

const StatTile = ({ title, value, icon: Icon, accent, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      width: '100%',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, #ffffff 60%)`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': {
        boxShadow: `0 8px 24px ${alpha(accent, 0.16)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="caption" sx={{ color: stormGrey[500], fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, color: accent, mt: 0.5 }}>
          {loading ? <CircularProgress size={22} sx={{ color: accent }} /> : value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.14),
          color: accent,
        }}
      >
        <Icon />
      </Box>
    </Stack>
  </Paper>
);

// Helper function to get the first name
const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return '';
  }
  return fullName.split(' ')[0];
};

const OrdersList = ({ onActionCompletes }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResendOtpModalOpen, setIsResendOtpModalOpen] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  const [selectedOrderForOtp, setSelectedOrderForOtp] = useState(null);
  const [selectedOrderForTimeline, setSelectedOrderForTimeline] = useState(null);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [smsStatuses, setSmsStatuses] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshingSms, setIsRefreshingSms] = useState(false);
  const [showCustomerPhone, setShowCustomerPhone] = useState(false);
  const [showCustomerAddress, setShowCustomerAddress] = useState(false);
  const recordsPerPage = 25;
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';
  const refreshIntervalRef = useRef(null);
  const smsStatusesRef = useRef({});

  // Get status and company from URL query params
  const queryParams = new URLSearchParams(location.search);
  const statusFilter = queryParams.get('status');
  const companyFilter = queryParams.get('company');
  const monthFilter = queryParams.get('month');
  const yearFilter = queryParams.get('year');

     const getStatusId = (statusName) => {
     switch (statusName) {
       case 'received':
         return 1;
       case 'dispatched':
         return 2;
       case 'delivered':
         return 3;
       case 'pending_sms_confirmation':
         return 4;
       default:
         return null;
     }
   };

     const getStatusText = (status_id) => {
     switch (status_id) {
       case 1:
         return 'Pending';
       case 2:
         return 'Dispatched';
       case 3:
         return 'Delivered';
       case 4:
         return 'Pending SMS Confirmation';
       default:
         return 'Unknown';
     }
   };

  const renderSmsStatus = (orderId) => {
    const smsStatus = smsStatuses[orderId];
    const chipSx = { fontWeight: 500, fontSize: '0.75rem' };

    if (!smsStatus) {
      return (
        <Chip
          label="Loading..."
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(stormGrey[400], 0.12), color: stormGrey[600] }}
        />
      );
    }

    if (!smsStatus.has_sms) {
      return (
        <Chip
          label="No SMS Sent"
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(stormGrey[400], 0.12), color: stormGrey[600] }}
        />
      );
    }

    if (smsStatus.sms_status === 'Failed') {
      return (
        <Chip
          label={`SMS Failed: ${smsStatus.failure_reason || 'Unknown'}`}
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(redOrange[500], 0.12), color: redOrange[500] }}
        />
      );
    }
    if (smsStatus.sms_status === 'Delivered') {
      return (
        <Chip
          label="SMS Delivered"
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(kepple[500], 0.12), color: kepple[600] }}
        />
      );
    }
    if (smsStatus.sms_status === 'Processing') {
      return (
        <Chip
          label="SMS Processing"
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(california[500], 0.12), color: california[500] }}
        />
      );
    }
    if (smsStatus.sms_status === 'Unknown') {
      return (
        <Chip
          label="SMS Status Unknown"
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(stormGrey[400], 0.12), color: stormGrey[600] }}
        />
      );
    }

    return (
      <Chip
        label={`SMS: ${smsStatus.sms_status || 'Unknown'}`}
        size="small"
        sx={{ ...chipSx, bgcolor: alpha(shakespeare[500], 0.12), color: shakespeare[500] }}
      />
    );
  };

  const getStatusChipSx = (statusId) => {
    switch (statusId) {
      case 1:
        return { bgcolor: alpha(california[500], 0.12), color: california[500] };
      case 2:
        return { bgcolor: alpha(shakespeare[500], 0.12), color: shakespeare[500] };
      case 3:
        return { bgcolor: alpha(kepple[500], 0.12), color: kepple[600] };
      case 4:
        return { bgcolor: alpha(neonBlue[500], 0.12), color: neonBlue[600] };
      default:
        return { bgcolor: alpha(stormGrey[400], 0.12), color: stormGrey[600] };
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const extractSmsStatusesFromOrders = useCallback((orderList) => {
    const extracted = {};
    orderList.forEach((order) => {
      if (order.sms_status) {
        extracted[order.id] = order.sms_status;
      }
    });
    return extracted;
  }, []);

  const fetchBatchSmsStatuses = useCallback(async (orderIds) => {
    const uniqueIds = [...new Set(orderIds.filter(Boolean))];
    if (uniqueIds.length === 0) {
      return;
    }

    try {
      const response = await getOrderSmsStatusesBatch(uniqueIds);
      setSmsStatuses((prev) => ({
        ...prev,
        ...response.data,
      }));
    } catch (error) {
      // ignore batch errors
    }
  }, []);

  const fetchOrders = useCallback(async (isBackgroundRefresh = false) => {
    if (isBackgroundRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      // Build query params for API call
      const params = {};
      if (companyFilter && isSuperAdmin) {
        params.company = companyFilter;
      }
      if (yearFilter) {
        params.year = yearFilter;
      }
      if (monthFilter) {
        params.month = monthFilter;
      }
      
      const response = await getOrders(params);
      setOrders(response.data);

      const smsFromOrders = extractSmsStatusesFromOrders(response.data);
      if (Object.keys(smsFromOrders).length > 0) {
        setSmsStatuses((prev) => ({ ...prev, ...smsFromOrders }));
      }

      const missingSmsIds = response.data
        .filter((order) => !order.sms_status)
        .slice(0, recordsPerPage)
        .map((order) => order.id);
      if (missingSmsIds.length > 0) {
        await fetchBatchSmsStatuses(missingSmsIds);
      }
    } catch (error) {
      if (!isBackgroundRefresh) {
        alert('Error fetching orders. Please try again.');
      }
    } finally {
      if (isBackgroundRefresh) {
        setIsRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [extractSmsStatusesFromOrders, fetchBatchSmsStatuses, companyFilter, monthFilter, yearFilter, isSuperAdmin]);

  useEffect(() => {
    smsStatusesRef.current = smsStatuses;
  }, [smsStatuses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [monthFilter, yearFilter, companyFilter, statusFilter]);

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Poll only orders with in-progress SMS delivery
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(() => {
      if (isLoading || isDetailsModalOpen || isEditModalOpen) {
        return;
      }

      const processingIds = orders
        .filter((order) => {
          const status = smsStatusesRef.current[order.id];
          return status && status.has_sms && status.sms_status === 'Processing';
        })
        .map((order) => order.id);

      if (processingIds.length > 0) {
        setIsRefreshingSms(true);
        fetchBatchSmsStatuses(processingIds).finally(() => {
          setIsRefreshingSms(false);
        });
      }
    }, 10000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [orders, fetchBatchSmsStatuses, isLoading, isDetailsModalOpen, isEditModalOpen]);

  // Manual refresh function
  const handleManualRefresh = useCallback(async () => {
    await fetchOrders();
  }, [fetchOrders]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []);

  const sortedAndFilteredOrders = useMemo(() => {
    let filtered = [...orders];

    if (statusFilter) {
      const statusId = getStatusId(statusFilter);
      if (statusId) {
        filtered = filtered.filter(order => order.status_id === statusId);
      }
    }

    if (searchQuery) {
      const searchTerm = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        Object.values(order).some(value =>
          String(value).toLowerCase().includes(searchTerm)
        ) ||
        getFirstName(order.customer_name)?.toLowerCase().includes(searchTerm) ||
        maskPhoneNumber(order.customer_phone)?.toLowerCase().includes(searchTerm) ||
        getStatusText(order.status_id)?.toLowerCase().includes(searchTerm)
      );
    }
    
    return filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'status_id') {
        aValue = getStatusText(a.status_id);
        bValue = getStatusText(b.status_id);
             } else if (orderBy === 'createdAt') {
         aValue = new Date(a.createdAt).getTime();
         bValue = new Date(b.createdAt).getTime();
         return order === 'asc' ? aValue - bValue : bValue - aValue;
       } else if (orderBy === 'delivered_at') {
         // Handle delivered_at sorting - orders without delivered_at come last
         if (!a.delivered_at && !b.delivered_at) return 0;
         if (!a.delivered_at) return 1; // a comes last
         if (!b.delivered_at) return -1; // b comes last
         
         aValue = new Date(a.delivered_at).getTime();
         bValue = new Date(b.delivered_at).getTime();
         return order === 'asc' ? aValue - bValue : bValue - aValue;
       }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [orders, orderBy, order, searchQuery, statusFilter]);

  const orderStats = useMemo(() => ({
    total: orders.length,
    pending: orders.filter((o) => o.status_id === 1).length,
    dispatched: orders.filter((o) => o.status_id === 2).length,
    delivered: orders.filter((o) => o.status_id === 3).length,
  }), [orders]);



  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  // Calculate the current records to display
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedAndFilteredOrders.slice(indexOfFirstRecord, indexOfLastRecord);

  const currentPageOrderIds = useMemo(() => (
    currentRecords.map((order) => order.id)
  ), [currentRecords]);

  useEffect(() => {
    const missingIds = currentPageOrderIds.filter((orderId) => !smsStatusesRef.current[orderId]);
    if (missingIds.length > 0) {
      fetchBatchSmsStatuses(missingIds);
    }
  }, [currentPageOrderIds, fetchBatchSmsStatuses]);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRowClick = async (order) => {
    setSelectedOrderForDetails(order);
    setIsDetailsModalOpen(true);
  };

  const handleOrderUpdated = () => {
    fetchOrders();
    if (onActionCompletes) {
      onActionCompletes();
    }
  };

  const handleEditClick = (order) => {
    const userRole = localStorage.getItem('userRole');
    
    // Check if user can edit this order based on role and status
    const canEdit = (userRole === 'clerk' && order.status_id === 1) || 
                   (userRole === 'cus-admin' && (order.status_id === 1 || order.status_id === 2));
    
    if (!canEdit) {
      const statusText = order.status_id === 1 ? 'Received' : 
                        order.status_id === 2 ? 'Dispatched' : 
                        order.status_id === 3 ? 'Delivered' : 
                        order.status_id === 4 ? 'Pending SMS Confirmation' : 'Unknown';
      
      if (userRole === 'cus-admin') {
        alert(`Order cannot be edited. Current status: ${statusText}. Customer admins can only edit orders with 'Received' or 'Dispatched' status.`);
      } else {
        alert(`Order cannot be edited. Current status: ${statusText}. Only orders with 'Received' status can be edited.`);
      }
      return;
    }
    
    setSelectedOrder(order);
    setIsEditModalOpen(true);
  };

  const handleResendOtpClick = (order) => {
    // Only allow resending OTP for orders that are in "dispatched" status (status_id === 2)
    if (order.status_id !== 2) {
      alert(`OTP can only be resent for orders in 'Dispatched' status. Current status: ${getStatusText(order.status_id)}`);
      return;
    }
    
    setSelectedOrderForOtp(order);
    setIsResendOtpModalOpen(true);
  };

  const handleTimelineClick = (e, order) => {
    e.stopPropagation();
    setSelectedOrderForTimeline(order);
    setIsTimelineModalOpen(true);
  };

  const renderSortLabel = (columnId, label) => (
    <TableSortLabel
      active={orderBy === columnId}
      direction={orderBy === columnId ? order : 'asc'}
      onClick={() => handleRequestSort(columnId)}
      sx={{ '& .MuiTableSortLabel-icon': { color: `${neonBlue[500]} !important` } }}
    >
      {label}
    </TableSortLabel>
  );

  const isEditableRow = (order) =>
    (userRole === 'clerk' && order.status_id === 1) ||
    ((order.status_id === 1 || order.status_id === 2) && userRole === 'cus-admin');

  const getOrderRowSx = (order, index) => ({
    cursor: isEditableRow(order) || order.status_id === 2 ? 'pointer' : 'default',
    bgcolor: index % 2 === 1 ? alpha(stormGrey[100], 0.5) : '#fff',
    ...(isEditableRow(order) && {
      boxShadow: `inset 3px 0 0 ${order.status_id === 1 ? neonBlue[500] : california[500]}`,
    }),
    '&:hover': {
      bgcolor: alpha(neonBlue[500], 0.06),
    },
    '& td': { borderBottom: `1px solid ${stormGrey[200]}` },
  });

  const getOrderRowAction = (order) => {
    if (userRole === 'clerk' && order.status_id === 1) {
      return () => handleEditClick(order);
    }
    if ((order.status_id === 1 || order.status_id === 2) && userRole === 'cus-admin') {
      return () => handleEditClick(order);
    }
    if (order.status_id === 2) {
      return () => handleResendOtpClick(order);
    }
    return () => handleRowClick(order);
  };

  const renderMobileOrderCard = (order, index) => (
    <Card
      key={order.id}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${stormGrey[200]}`,
        ...getOrderRowSx(order, index),
      }}
      onClick={getOrderRowAction(order)}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {order.waybill_no}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.order_no || 'No order no.'}
            </Typography>
          </Box>
          <Chip
            label={getStatusText(order.status_id)}
            size="small"
            sx={{ fontWeight: 500, ...getStatusChipSx(order.status_id) }}
          />
        </Box>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            <strong>Customer:</strong>{' '}
            {order.status_id === 3 ? getFirstName(order.customer_name) : order.customer_name}
          </Typography>
          {showCustomerPhone && (
            <Typography variant="body2">
              <strong>Phone:</strong>{' '}
              {order.status_id === 3
                ? maskPhoneNumber(order.customer_phone)
                : order.customer_phone}
            </Typography>
          )}
          {showCustomerAddress && (
            <Typography variant="body2">
              <strong>Address:</strong> {order.customer_address}
            </Typography>
          )}
          {isSuperAdmin && (
            <Typography variant="body2">
              <strong>Company:</strong> {order.Company?.name || 'N/A'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(order.createdAt).toLocaleString()}
          </Typography>
          {order.delivered_at && (
            <Typography variant="caption" color="text.secondary">
              Delivered: {new Date(order.delivered_at).toLocaleString()}
            </Typography>
          )}
        </Stack>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          {renderSmsStatus(order.id)}
          <Tooltip title="View Order Timeline">
            <IconButton
              onClick={(e) => handleTimelineClick(e, order)}
              size="small"
              sx={{
                color: neonBlue[500],
                bgcolor: alpha(neonBlue[500], 0.08),
                '&:hover': { bgcolor: alpha(neonBlue[500], 0.16) },
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2.5} sx={{ mb: 3, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Total Orders"
            value={orderStats.total}
            icon={AssignmentIcon}
            accent={neonBlue[500]}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Pending"
            value={orderStats.pending}
            icon={InboxIcon}
            accent={california[500]}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Dispatched"
            value={orderStats.dispatched}
            icon={LocalShippingIcon}
            accent={shakespeare[500]}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Delivered"
            value={orderStats.delivered}
            icon={CheckCircleOutlineIcon}
            accent={kepple[500]}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {statusFilter && (
        <Box mb={2}>
          <Chip
            label={`Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
            onDelete={() => {
              const queryParams = new URLSearchParams(location.search);
              queryParams.delete('status');
              const newSearch = queryParams.toString();
              navigate(`/orders${newSearch ? `?${newSearch}` : ''}`, { replace: true });
            }}
            sx={{
              bgcolor: alpha(neonBlue[500], 0.08),
              color: neonBlue[600],
              borderColor: alpha(neonBlue[500], 0.3),
              fontWeight: 500,
            }}
            variant="outlined"
          />
        </Box>
      )}

      {yearFilter && (
        <Box mb={2}>
          <Chip
            label={
              monthFilter
                ? `Period: ${new Date(Number(yearFilter), Number(monthFilter) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                : `Year: ${yearFilter}`
            }
            onDelete={() => {
              const queryParams = new URLSearchParams(location.search);
              queryParams.delete('month');
              queryParams.delete('year');
              const newSearch = queryParams.toString();
              navigate(`/orders${newSearch ? `?${newSearch}` : ''}`, { replace: true });
            }}
            sx={{
              bgcolor: alpha(neonBlue[500], 0.08),
              color: neonBlue[600],
              borderColor: alpha(neonBlue[500], 0.3),
              fontWeight: 500,
            }}
            variant="outlined"
          />
        </Box>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: neonBlue[500] }} />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          bgcolor: '#fff',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search orders (waybill, order no., name, phone, address, status)..."
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: stormGrey[400] }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: stormGrey[50],
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleManualRefresh}
            disabled={isLoading || isRefreshing}
            sx={{
              minWidth: { xs: '100%', sm: 120 },
              borderColor: alpha(neonBlue[500], 0.4),
              color: neonBlue[600],
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: neonBlue[500],
                bgcolor: alpha(neonBlue[500], 0.04),
              },
            }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>

        {isRefreshingSms && (
          <Box display="flex" alignItems="center" mt={1.5} gap={1}>
            <CircularProgress size={16} sx={{ color: neonBlue[500] }} />
            <Typography variant="caption" color="text.secondary">
              Refreshing SMS statuses...
            </Typography>
          </Box>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} mt={1.5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showCustomerPhone}
                onChange={(e) => setShowCustomerPhone(e.target.checked)}
                size="small"
                sx={{ color: neonBlue[500], '&.Mui-checked': { color: neonBlue[500] } }}
              />
            }
            label={<Typography variant="body2">Show Customer Phone</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showCustomerAddress}
                onChange={(e) => setShowCustomerAddress(e.target.checked)}
                size="small"
                sx={{ color: neonBlue[500], '&.Mui-checked': { color: neonBlue[500] } }}
              />
            }
            label={<Typography variant="body2">Show Customer Address</Typography>}
          />
        </Stack>
      </Paper>

      {!isLoading && currentRecords.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            py: 4,
            borderRadius: 2,
            border: `1px solid ${stormGrey[200]}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No orders found.
          </Typography>
        </Paper>
      )}

      {!isLoading && currentRecords.length > 0 && isMobile && (
        <Stack spacing={1.5}>
          {currentRecords.map((order, index) => renderMobileOrderCard(order, index))}
        </Stack>
      )}

      {!isLoading && currentRecords.length > 0 && !isMobile && (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          overflow: 'hidden',
        }}
      >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(neonBlue[500], 0.06) }}>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '90px', whiteSpace: 'nowrap' }}>{renderSortLabel('waybill_no', 'Waybill No.')}</TableCell>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '90px', whiteSpace: 'nowrap' }}>{renderSortLabel('order_no', 'Order No.')}</TableCell>
            {isSuperAdmin && <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '100px' }}>Company</TableCell>}
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '120px' }}>{renderSortLabel('customer_name', 'Customer Name')}</TableCell>
            {showCustomerPhone && <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '110px', whiteSpace: 'nowrap' }}>{renderSortLabel('customer_phone', 'Customer Phone')}</TableCell>}
            {showCustomerAddress && <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '130px' }}>{renderSortLabel('customer_address', 'Customer Address')}</TableCell>}
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '90px', whiteSpace: 'nowrap' }} align="center">{renderSortLabel('status_id', 'Status')}</TableCell>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '140px', whiteSpace: 'nowrap' }}>{renderSortLabel('createdAt', 'Date Created')}</TableCell>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '140px', whiteSpace: 'nowrap' }}>{renderSortLabel('delivered_at', 'Delivered Date')}</TableCell>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: '90px', whiteSpace: 'nowrap' }} align="center">SMS Status</TableCell>
            <TableCell sx={{ fontWeight: 600, color: stormGrey[700], minWidth: 80, whiteSpace: 'nowrap' }} align="center">Timeline</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentRecords.map((order, index) => (
                         <TableRow
               key={order.id}
               hover
               onClick={getOrderRowAction(order)}
               sx={getOrderRowSx(order, index)}
             >
              <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8125rem' }}>{order.waybill_no}</TableCell>
              <TableCell sx={{ color: stormGrey[700] }}>{order.order_no}</TableCell>
              {isSuperAdmin && (
                <TableCell sx={{ color: stormGrey[600] }}>{order.Company?.name || 'N/A'}</TableCell>
              )}
              <TableCell sx={{ fontWeight: 500 }}>
                {order.status_id === 3 ? getFirstName(order.customer_name) : order.customer_name}
              </TableCell>
              {showCustomerPhone && (
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {order.status_id === 3 ? maskPhoneNumber(order.customer_phone) : order.customer_phone}
                </TableCell>
              )}
              {showCustomerAddress && (
                <TableCell
                  sx={{
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: stormGrey[600],
                  }}
                  title={order.customer_address}
                >
                  {order.customer_address}
                </TableCell>
              )}
              <TableCell align="center">
                <Stack alignItems="center" spacing={0.5}>
                  <Chip
                    label={getStatusText(order.status_id)}
                    size="small"
                    sx={{ fontWeight: 500, ...getStatusChipSx(order.status_id) }}
                  />
                  {order.status_id === 2 && (
                    <Typography variant="caption" sx={{ color: neonBlue[500], fontSize: '0.7rem' }}>
                      Click to resend OTP
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: stormGrey[600], fontSize: '0.8125rem' }}>
                {new Date(order.createdAt).toLocaleString()}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: stormGrey[600], fontSize: '0.8125rem' }}>
                {order.delivered_at ? new Date(order.delivered_at).toLocaleString() : '-'}
              </TableCell>
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                {renderSmsStatus(order.id)}
              </TableCell>
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <Tooltip title="View Order Timeline">
                  <IconButton
                    onClick={(e) => handleTimelineClick(e, order)}
                    size="small"
                    sx={{
                      color: neonBlue[500],
                      bgcolor: alpha(neonBlue[500], 0.08),
                      '&:hover': { bgcolor: alpha(neonBlue[500], 0.16) },
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      )}

      {/* Pagination */}
      {sortedAndFilteredOrders.length > recordsPerPage && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={Math.ceil(sortedAndFilteredOrders.length / recordsPerPage)}
            page={currentPage}
            onChange={(event, value) => paginate(value)}
            color="primary"
          />
        </Box>
      )}


      
      {/* Edit Order Modal */}
      {selectedOrder && (
        <EditOrderModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onOrderUpdated={() => {
            handleOrderUpdated();
            setIsEditModalOpen(false);
            setSelectedOrder(null);
          }}
        />
      )}



      {/* Resend OTP Modal */}
      {selectedOrderForOtp && (
        <ResendOtpModal
          open={isResendOtpModalOpen}
          onClose={() => {
            setIsResendOtpModalOpen(false);
            setSelectedOrderForOtp(null);
          }}
          order={selectedOrderForOtp}
          onOtpResent={() => {
            // Refresh orders and SMS statuses
            fetchOrders();
            if (onActionCompletes) {
              onActionCompletes();
            }
          }}
        />
      )}

      {/* Order Details Modal */}
      {selectedOrderForDetails && (
        <OrderDetailsModal
          open={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedOrderForDetails(null);
          }}
          order={selectedOrderForDetails}
          onOrderUpdated={() => {
            if (onActionCompletes) {
              onActionCompletes();
            }
            setIsDetailsModalOpen(false);
            setSelectedOrderForDetails(null);
          }}
        />
      )}

      {/* Order Timeline Modal */}
      {selectedOrderForTimeline && (
        <OrderTimelineModal
          open={isTimelineModalOpen}
          onClose={() => {
            setIsTimelineModalOpen(false);
            setSelectedOrderForTimeline(null);
          }}
          order={selectedOrderForTimeline}
        />
      )}
    </Box>
  );
};

export default OrdersList;